import { createContext, useContext } from "react"
import { cn } from "cn"

import { selectIsXS } from "@/lib/storage/app-slice"
import { useAppSelector } from "@/lib/storage/hooks"

type MessageCardLayout = "chat" | "report"
type MessageCardArrangement = "stacked" | "floating"

/**
 * How a message card places its photo when it floats beside the text. Chat leaves this
 * unset (default `"chat"`): smaller photo, float right. Agent-run reports wrap
 * SingleMessage in `"report"` so the photo leads at ~47% width.
 */
export const MessageCardLayoutContext = createContext<MessageCardLayout>("chat")

/**
 * Overrides whether message content drives the map's hover, for a page that keeps the
 * `"report"` layout but sits beside a map of its own (the expanded ideas sheet).
 */
export const MessageTracksHoveredPlaceContext = createContext<boolean | null>(
  null
)

/**
 * Whether message content may drive the map's hovered place. Only chat sits beside the
 * chat map; reports and other pages that reuse message cards must not touch its hover.
 */
export function useMessageTracksHoveredPlace() {
  const override = useContext(MessageTracksHoveredPlaceContext)
  const layout = useContext(MessageCardLayoutContext)
  return override ?? layout === "chat"
}

/**
 * The frame every `<card>` in a message sits in — a place card or a web event card alike — with
 * the rules that draw one divider between adjacent cards and space them from the text around
 * them. Shared so the two kinds of card line up as one list. The column gap reads a variable
 * the layout table may set; 1rem when it doesn't.
 */
const CARD_BLOCK_FRAME = cn(
  "flex flex-wrap gap-x-(--msg-card-col-gap,1rem) gap-y-2.5 border-black-a-1 not-first:mt-6 not-last:mb-6 not-last:border-b not-last:pb-6 not-first:not-[[data-slot=message-card-block]+&]:border-t not-first:not-[[data-slot=message-card-block]+&]:pt-6 dark:border-white-a-1 [&+hr]:hidden"
)

/**
 * The block and photo-slot classes for each layout × arrangement. Each cell is complete —
 * read one cell and you know everything that case adds; no cell overrides another.
 */
const STACKED_LAYOUT = {
  block: cn("flex-col"),
  photo: cn("mt-2 w-full max-w-96"),
} satisfies { block: string; photo: string }

const MESSAGE_CARD_LAYOUT_CLASSES = {
  chat: {
    stacked: STACKED_LAYOUT,
    floating: { block: cn("flex-row"), photo: cn("w-1/3 min-w-40") },
  },
  report: {
    stacked: STACKED_LAYOUT,
    floating: {
      // Column gap grows with the card: 16px in a narrow sheet, up to 32px when there's room.
      block: cn("flex-row [--msg-card-col-gap:clamp(16px,4%,32px)]"),
      photo: cn("order-first w-[47%] shrink-0"),
    },
  },
} satisfies Record<
  MessageCardLayout,
  Record<MessageCardArrangement, { block: string; photo: string }>
>

/** Stacked on XS; otherwise photo floats beside the text (side and size from layout). */
export function useMessageCardLayout() {
  const isXS = useAppSelector(selectIsXS)
  const layout = useContext(MessageCardLayoutContext)
  const arrangement: MessageCardArrangement = isXS ? "stacked" : "floating"
  const { block, photo } = MESSAGE_CARD_LAYOUT_CLASSES[layout][arrangement]
  return {
    isFloating: arrangement === "floating",
    blockClassName: cn(CARD_BLOCK_FRAME, block),
    photoClassName: cn("not-prose", photo),
  }
}
